import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Rss, Plus, Trash2, RefreshCw, Edit, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newFeed, setNewFeed] = useState({ name: '', url: '' });
  const [addingFeed, setAddingFeed] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', url: '' });
  const [selectedFeeds, setSelectedFeeds] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        
        // Verificar se é admin antes de buscar dados do dashboard
        if (userRes.data.email !== 'fablucvi@gmail.com') {
          alert('Acesso negado. Apenas o administrador pode acessar o dashboard.');
          window.location.href = '/';
          return;
        }

        const [feedsRes, postsRes] = await Promise.all([
          axios.get(`${API}/rss-feeds`, { withCredentials: true }),
          axios.get(`${API}/posts`, { params: { limit: 100 } })
        ]);

        setFeeds(feedsRes.data);
        setMyPosts(postsRes.data.filter((p) => p.author_id === userRes.data.user_id));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status === 403) {
          alert('Acesso negado. Apenas o administrador pode acessar o dashboard.');
          window.location.href = '/';
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddFeed = async (e) => {
    e.preventDefault();
    if (!newFeed.name || !newFeed.url) return;

    setAddingFeed(true);
    try {
      const response = await axios.post(`${API}/rss-feeds`, newFeed, {
        withCredentials: true
      });

      setFeeds([...feeds, response.data]);
      setNewFeed({ name: '', url: '' });
      alert('Feed RSS adicionado com sucesso!');
    } catch (error) {
      console.error('Error adding feed:', error);
      alert('Erro ao adicionar feed RSS. Verifique a URL.');
    } finally {
      setAddingFeed(false);
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('Deseja deletar este feed RSS?')) return;

    try {
      await axios.delete(`${API}/rss-feeds/${feedId}`, { withCredentials: true });
      setFeeds(feeds.filter((f) => f.feed_id !== feedId));
    } catch (error) {
      console.error('Error deleting feed:', error);
      alert('Erro ao deletar feed');
    }
  };

  const handleEditFeed = (feed) => {
    setEditingFeed(feed.feed_id);
    setEditForm({ name: feed.name, url: feed.url });
  };

  const handleUpdateFeed = async (feedId) => {
    if (!editForm.name || !editForm.url) return;

    try {
      const response = await axios.put(
        `${API}/rss-feeds/${feedId}`,
        editForm,
        { withCredentials: true }
      );

      setFeeds(feeds.map((f) => (f.feed_id === feedId ? response.data : f)));
      setEditingFeed(null);
      setEditForm({ name: '', url: '' });
      alert('Feed atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating feed:', error);
      alert('Erro ao atualizar feed. Verifique a URL.');
    }
  };

  const handleCancelEdit = () => {
    setEditingFeed(null);
    setEditForm({ name: '', url: '' });
  };

  const toggleFeedSelection = (feedId) => {
    setSelectedFeeds(prev =>
      prev.includes(feedId) ? prev.filter(id => id !== feedId) : [...prev, feedId]
    );
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts(prev =>
      prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
    );
  };

  const handleDeleteSelectedFeeds = async () => {
    if (selectedFeeds.length === 0) return;
    if (!window.confirm(`Deseja deletar ${selectedFeeds.length} feed(s) selecionado(s)?`)) return;

    try {
      await Promise.all(
        selectedFeeds.map(feedId =>
          axios.delete(`${API}/rss-feeds/${feedId}`, { withCredentials: true })
        )
      );
      setFeeds(feeds.filter(f => !selectedFeeds.includes(f.feed_id)));
      setSelectedFeeds([]);
      alert('Feeds deletados com sucesso!');
    } catch (error) {
      console.error('Error deleting feeds:', error);
      alert('Erro ao deletar alguns feeds');
    }
  };

  const handleDeleteSelectedPosts = async () => {
    if (selectedPosts.length === 0) return;
    if (!window.confirm(`Deseja deletar ${selectedPosts.length} post(s) selecionado(s)?`)) return;

    try {
      await Promise.all(
        selectedPosts.map(postId =>
          axios.delete(`${API}/posts/${postId}`, { withCredentials: true })
        )
      );
      setMyPosts(myPosts.filter(p => !selectedPosts.includes(p.post_id)));
      setSelectedPosts([]);
      alert('Posts deletados com sucesso!');
    } catch (error) {
      console.error('Error deleting posts:', error);
      alert('Erro ao deletar alguns posts');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Deseja deletar este post?')) return;

    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setMyPosts(myPosts.filter((p) => p.post_id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Erro ao deletar post');
    }
  };

  const handleRefreshFeeds = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/rss-feeds/refresh`, {}, {
        withCredentials: true
      });
      alert(response.data.message);
      window.location.reload();
    } catch (error) {
      console.error('Error refreshing feeds:', error);
      alert('Erro ao atualizar feeds');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4 tracking-tight leading-tight">
            Dashboard
          </h1>
          <p className="text-lg text-stone-700 leading-relaxed">
            Gerencie seus feeds RSS e posts
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Rss className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <h2 className="text-2xl font-heading font-semibold">Feeds RSS</h2>
              </div>
              <div className="flex items-center gap-2">
                {selectedFeeds.length > 0 && (
                  <Button
                    onClick={handleDeleteSelectedFeeds}
                    size="sm"
                    variant="destructive"
                    className="rounded-md"
                    data-testid="delete-selected-feeds"
                  >
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Deletar ({selectedFeeds.length})
                  </Button>
                )}
                <Button
                  onClick={handleRefreshFeeds}
                  disabled={refreshing}
                  size="sm"
                  variant="ghost"
                  className="rounded-md"
                  data-testid="refresh-feeds-button"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
                    strokeWidth={1.5}
                  />
                </Button>
              </div>
            </div>

            <form onSubmit={handleAddFeed} className="space-y-3 mb-6" data-testid="add-feed-form">
              <Input
                type="text"
                placeholder="Nome do feed"
                value={newFeed.name}
                onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                className="rounded-md"
                data-testid="feed-name-input"
              />
              <Input
                type="url"
                placeholder="URL do feed RSS"
                value={newFeed.url}
                onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                className="rounded-md"
                data-testid="feed-url-input"
              />
              <Button
                type="submit"
                disabled={addingFeed || !newFeed.name || !newFeed.url}
                className="w-full rounded-md"
                data-testid="add-feed-button"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Adicionar Feed
              </Button>
            </form>

            <div className="space-y-3" data-testid="feeds-list">
              {feeds.length === 0 ? (
                <p className="text-stone-500 text-center py-4">Nenhum feed adicionado</p>
              ) : (
                feeds.map((feed) => (
                  <div
                    key={feed.feed_id}
                    className="p-4 bg-stone-50 rounded-md border border-stone-100"
                    data-testid={`feed-${feed.feed_id}`}
                  >
                    {editingFeed === feed.feed_id ? (
                      <div className="space-y-3">
                        <Input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="Nome do feed"
                          className="rounded-md"
                        />
                        <Input
                          type="url"
                          value={editForm.url}
                          onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                          placeholder="URL do feed RSS"
                          className="rounded-md"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleUpdateFeed(feed.feed_id)}
                            size="sm"
                            className="rounded-md"
                          >
                            Salvar
                          </Button>
                          <Button
                            onClick={handleCancelEdit}
                            size="sm"
                            variant="ghost"
                            className="rounded-md"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedFeeds.includes(feed.feed_id)}
                            onChange={() => toggleFeedSelection(feed.feed_id)}
                            className="w-4 h-4 text-primary rounded cursor-pointer"
                            data-testid={`checkbox-feed-${feed.feed_id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{feed.name}</p>
                            <p className="text-sm text-stone-500 truncate">{feed.url}</p>
                            {feed.last_fetched_at && (
                              <p className="text-xs text-stone-400 mt-1">
                                Última atualização:{' '}
                                {formatDistanceToNow(new Date(feed.last_fetched_at), {
                                  addSuffix: true,
                                  locale: ptBR
                                })}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleEditFeed(feed)}
                              variant="ghost"
                              size="sm"
                              className="rounded-md"
                              data-testid={`edit-feed-${feed.feed_id}`}
                            >
                              <Edit className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                            <Button
                              onClick={() => handleDeleteFeed(feed.feed_id)}
                              variant="ghost"
                              size="sm"
                              className="rounded-md text-destructive hover:text-destructive"
                              data-testid={`delete-feed-${feed.feed_id}`}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <h2 className="text-2xl font-heading font-semibold">Meus Posts</h2>
              </div>
              <div className="flex items-center gap-2">
                {selectedPosts.length > 0 && (
                  <Button
                    onClick={handleDeleteSelectedPosts}
                    size="sm"
                    variant="destructive"
                    className="rounded-md"
                    data-testid="delete-selected-posts"
                  >
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Deletar ({selectedPosts.length})
                  </Button>
                )}
                <Link to="/create-post">
                  <Button size="sm" className="rounded-md" data-testid="create-post-button">
                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Novo
                  </Button>
                </Link>
              </div>
            </div>

            <div className="space-y-3" data-testid="my-posts-list">
              {myPosts.length === 0 ? (
                <p className="text-stone-500 text-center py-4">Nenhum post criado</p>
              ) : (
                myPosts.map((post) => (
                  <div
                    key={post.post_id}
                    className="p-4 bg-stone-50 rounded-md border border-stone-100"
                    data-testid={`my-post-${post.post_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedPosts.includes(post.post_id)}
                        onChange={() => togglePostSelection(post.post_id)}
                        className="w-4 h-4 text-primary rounded cursor-pointer mt-1"
                        data-testid={`checkbox-post-${post.post_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground line-clamp-1">{post.title}</p>
                        <p className="text-sm text-stone-500 mt-1">
                          {formatDistanceToNow(new Date(post.published_at), {
                            addSuffix: true,
                            locale: ptBR
                          })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/post/${post.post_id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-md"
                            data-testid={`view-post-${post.post_id}`}
                          >
                            <Eye className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </Link>
                        <Link to={`/edit-post/${post.post_id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-md"
                            data-testid={`edit-post-${post.post_id}`}
                          >
                            <Edit className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </Link>
                        <Button
                          onClick={() => handleDeletePost(post.post_id)}
                          variant="ghost"
                          size="sm"
                          className="rounded-md text-destructive hover:text-destructive"
                          data-testid={`delete-post-${post.post_id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;